import clsx from 'clsx';

interface Props {
  text: string;
  /** Optional substring to highlight (case-insensitive). Search match. */
  match?: string;
  className?: string;
  /** Min length before the text gets split at all. Below this, render as-is.
   * Default 15 — short names like "Hive-Go" stay whole. */
  splitThreshold?: number;
  /** Fallback character count for the always-visible suffix when there's no
   * word boundary to split on. Default 8. */
  tailLength?: number;
}

/** Tail length cap when matching the `<word> <number>` suffix (e.g. "Floor 16").
 * Picks a value that comfortably fits canonical labels without eating into
 * the shrinkable head. */
const LABELED_INDEX_MAX_TAIL = 15;

/**
 * Pick a (head, tail) split for middle-ellipsis. The goal is to keep the
 * distinguishing identifier visible on the right while the long shared prefix
 * shrinks with `…`. Priority:
 *   1. A trailing `<word> <number>` pair like "Floor 16" — pin both together
 *      so the user never sees a bare "16" detached from its label.
 *   2. The last whitespace-delimited token, if it's a sensible size.
 *   3. Fall back to a fixed character count.
 */
function splitText(text: string, splitThreshold: number, tailLength: number) {
  if (text.length <= splitThreshold) return { head: text, tail: '' };

  // Priority 1: `<word> <number>` tail. Matches identifiers like "Floor 16",
  // "Suite 100", "Cold-Storage 5". The label must be pure-alphabetic
  // (hyphen-joinable), so `_` doesn't glue it to the long prefix —
  // "Snowflake Atlanta_-_LOC114_-_Floor 16" captures only "Floor 16",
  // not the whole "_-_Floor 16" run. Length capped by
  // LABELED_INDEX_MAX_TAIL so the head still has room to shrink.
  const labeledIndex = text.match(/[A-Za-z]+(?:-[A-Za-z]+)*\s+\d+\s*$/);
  if (labeledIndex && labeledIndex[0].length <= LABELED_INDEX_MAX_TAIL) {
    const tail = labeledIndex[0];
    return { head: text.slice(0, text.length - tail.length), tail };
  }

  // Priority 2: last whitespace token.
  const lastSpace = text.lastIndexOf(' ');
  const suffixFromSpace = text.length - lastSpace - 1;
  if (lastSpace > 0 && suffixFromSpace >= 2 && suffixFromSpace <= 15) {
    return { head: text.slice(0, lastSpace + 1), tail: text.slice(lastSpace + 1) };
  }

  // Priority 3: fixed character count.
  return { head: text.slice(0, -tailLength), tail: text.slice(-tailLength) };
}

/** Render a slice of the original text, marking the portion that intersects
 * the `[matchStart, matchEnd)` range (relative to the full text) with the
 * yellow highlight. Works correctly when the match straddles the head/tail
 * split — the caller passes the same global match range to both halves and
 * each half highlights only its overlapping portion.
 *
 * `sliceOffset` is the global position of `slice[0]` in the original text.
 */
function renderWithHighlight(
  slice: string,
  sliceOffset: number,
  matchStart: number,
  matchEnd: number,
) {
  if (matchStart < 0 || matchEnd <= matchStart) return slice;
  const localStart = Math.max(0, matchStart - sliceOffset);
  const localEnd = Math.min(slice.length, matchEnd - sliceOffset);
  if (localStart >= slice.length || localEnd <= 0 || localStart >= localEnd) return slice;
  return (
    <>
      {slice.slice(0, localStart)}
      <span className="bg-yellow-300 text-gray-900 rounded">
        {slice.slice(localStart, localEnd)}
      </span>
      {slice.slice(localEnd)}
    </>
  );
}

/**
 * Single-line text with middle ellipsis — the start can shrink with `…`
 * while the trailing identifier stays pinned and fully visible. Useful for
 * sidebar tree rows where names share a long prefix and the distinguishing
 * detail is at the end ("Snowflake Atlanta_-_LOC114_-_Floor 16" truncates
 * to "Snowflake Atlanta_-_…Floor 16", keeping "Floor 16" as a unit).
 *
 * Implementation: flex row with a shrinkable head (CSS ellipsis) + a
 * non-shrinking tail. The match-highlight index is computed against the
 * full text once and applied independently to each half, so a search term
 * that crosses the head/tail boundary still renders correctly.
 */
export default function MiddleTruncatedText({
  text,
  match = '',
  className,
  splitThreshold = 15,
  tailLength = 8,
}: Props) {
  // Defensive coercion: upstream `name` fields can arrive as null/undefined
  // (GraphQL nullability + Apollo cache writes pre-data). Nameless nodes are
  // intentionally kept by `useFilteredSites` on empty filter, so they reach
  // this component; we render an empty span rather than crashing on `.length`.
  const safeText = text ?? '';
  const { head, tail } = splitText(safeText, splitThreshold, tailLength);

  // Compute the match range against the FULL text once. Each half then
  // highlights only its overlapping portion — a match that straddles the
  // split renders as two highlighted segments that visually read as one.
  const matchStart = match ? safeText.toLowerCase().indexOf(match.toLowerCase()) : -1;
  const matchEnd = matchStart === -1 ? -1 : matchStart + match.length;

  if (!tail) {
    return <span className={className}>{renderWithHighlight(head, 0, matchStart, matchEnd)}</span>;
  }

  return (
    <span className={clsx('inline-flex min-w-0 items-baseline', className)}>
      <span className="overflow-hidden text-ellipsis whitespace-nowrap min-w-0">
        {renderWithHighlight(head, 0, matchStart, matchEnd)}
      </span>
      <span className="whitespace-nowrap shrink-0">
        {renderWithHighlight(tail, head.length, matchStart, matchEnd)}
      </span>
    </span>
  );
}
